import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Link,
  Outlet,
  useLocation,
  useMatch,
  useParams,
} from "react-router-dom";
import {
  Building2,
  ChevronDown,
  Crown,
  FileText,
  CameraIcon,
  Hammer,
  Home,
  LayoutGrid,
  Users,
} from "lucide-react";
import { OrganizationType } from "common/constants";
import { MoncoLogoLink } from "components/common/MoncoLogoLink";
import { useProjectScope } from "hooks/useProjectScope";
import { useSidebarProjects } from "hooks/useSidebarProjects";
import { bugsnagClient } from "../bugsnag";
import type { NotifiableError } from "@bugsnag/js";
import api from "../utils/api";

const AuthedLayout = () => {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const { id: projectId } = useParams();
  const [user, setUser] = useState<{
    _id: string;
    name: string;
    organizationType: string;
  } | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [organization, setOrganization] = useState(null);
  const [organizationId, setOrganizationId] = useState<number | null>(null);
  const [userRole, setUserRole] = useState("");
  const [isGlobalAdmin, setIsGlobalAdmin] = useState(false);
  const [isUserLoaded, setIsUserLoaded] = useState(false);

  const [showLangDropdown, setShowLangDropdown] = useState(false);

  const isUserQuantitySurveyor =
    user?.organizationType === OrganizationType.QUANTITY_SURVEYOR;

  const canScopeToOrganization = isGlobalAdmin && Boolean(organizationId);
  const { organizationFilter } = useProjectScope({
    canScopeToOrganization,
    organizationId,
  });

  const {
    projects: userProjects,
    isPending: areProjectsPending,
    error: projectsError,
  } = useSidebarProjects({
    organization: organizationFilter,
    enabled: isUserLoaded,
  });

  // To-Do: Remove this logic after refactoring layout
  const isProgressReportPage = useMatch(
    "/project/:id/sections/:sectionId/progress-report",
  );
  const isProjectDetailsPage = useMatch("/project/:id");
  const removePadding = isProgressReportPage || isProjectDetailsPage;

  const fetchUser = async () => {
    try {
      const data = await api.fetchUser();
      setUser(data);
      setIsGlobalAdmin(data.isGlobalAdmin || false);
      setOrganizationId(data.organizationId ?? null);
      // Scope inputs are ready — let the project list fetch now, in parallel
      // with the organization lookup below.
      setIsUserLoaded(true);

      // Try to fetch organization data
      try {
        const orgData = await api.getMyOrganization();
        setOrganization(orgData.organization);
        setUserRole(orgData.userRole);
      } catch (orgError) {
        // User might not be part of an organization yet
        console.log("User not part of organization yet");
        bugsnagClient.notify(orgError as NotifiableError);
      }
    } catch (error) {
      bugsnagClient.notify(error as NotifiableError);
      console.error("Error fetching user:", error);
      // Still let the project list fetch (unscoped) if the user lookup failed.
      setIsUserLoaded(true);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  useEffect(() => {
    if (!projectsError) return;
    console.error("Error fetching projects for sidebar:", projectsError);
    bugsnagClient.notify(projectsError as NotifiableError);
  }, [projectsError]);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  // Demo projects that are always shown
  const demoProjects: { id: string; title: string }[] = [
    // {
    //   id: "demo",
    //   title: "Munich Urban Colab Demo",
    // },
    // {
    //   id: "u-rob-demo",
    //   title: "U-Rob Offices Drone Demo",
    // },
  ];

  return (
    <div className="flex flex-col h-screen bg-gray-50 md:flex-row">
      {/* Mobile Header */}
      <div className="md:hidden bg-white border p-4 flex justify-between items-center">
        <MoncoLogoLink className="w-32" />
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            {isMobileMenuOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            )}
          </svg>
        </button>
      </div>
      {/* Sidebar */}
      <div
        className={`
          ${isMobileMenuOpen ? "block" : "hidden"}
          md:block md:w-64 bg-white border-r
          md:relative w-full md:h-full z-20
          absolute h-full overflow-y-auto
        `}
      >
        <div className="p-4 hidden md:block">
          <MoncoLogoLink className="w-32" />
        </div>
        {/* Mobile Close Button */}
        <div className="md:hidden flex justify-end p-2">
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="p-2 rounded-full hover:bg-gray-200"
            aria-label="Close menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <nav className="mt-4 px-2">
          <Link
            to="/"
            className={`flex items-center py-2.5 px-4 rounded-md transition-colors duration-150 text-sm font-medium ${
              location.pathname === "/"
                ? "bg-blue-100 text-blue-700 font-semibold"
                : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"
            }`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <Home className="w-4 h-4 mr-3" />
            {t("overview")}
          </Link>
          {/* Global Admin Dashboard */}
          {isGlobalAdmin && (
            <Link
              to="/admin"
              className={`flex items-center py-2.5 px-4 rounded-md transition-colors duration-150 text-sm font-medium ${
                location.pathname === "/admin"
                  ? "bg-blue-100 text-blue-700 font-semibold"
                  : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <Crown className="w-4 h-4 mr-3 text-yellow-500" />
              Global Admin
            </Link>
          )}
          {/* Organization Management */}
          {organization && (userRole === "admin" || userRole === "manager") && (
            <Link
              to="/organization"
              className={`flex items-center py-2.5 px-4 rounded-md transition-colors duration-150 text-sm font-medium ${
                location.pathname === "/organization"
                  ? "bg-blue-100 text-blue-700 font-semibold"
                  : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <Building2 className="w-4 h-4 mr-3" />
              {t("organization")}
            </Link>
          )}
          {/* Divider */}
          <div className="my-4 border-t border-gray-200"></div>
          <div className="px-4 pb-2">
            <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
              {t("projects")}
            </h3>
          </div>
          {/* Demo Projects */}
          {demoProjects.map((project) => (
            <Link
              key={`demo-${project.id}`}
              to={`/project/${project.id}`}
              className="block py-2 px-4 text-gray-600 hover:bg-gray-200"
            >
              {project.title}
            </Link>
          ))}
          {/* User Projects */}
          {areProjectsPending ? (
            <div className="py-3 px-4 text-gray-400 italic text-sm flex items-center">
              <svg
                className="animate-spin h-4 w-4 mr-2 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              {t("loading_projects")}
            </div>
          ) : projectsError && userProjects.length === 0 ? (
            <div className="py-2 px-4 text-red-500 text-sm flex items-center">
              <svg
                className="w-4 h-4 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              {t("failed_to_load_projects")}
            </div>
          ) : userProjects.length > 0 ? (
            <div className="space-y-1">
              {userProjects.map((project) => {
                const isActive = project._id === Number(projectId);
                const linkUrl = isUserQuantitySurveyor
                  ? `/project/${project._id}/smart-surveyor/upload`
                  : `/project/${project._id}/sections`;

                return (
                  <div key={`user-${project._id}`}>
                    <Link
                      to={linkUrl}
                      className={`block py-2.5 px-4 rounded-md mx-2 transition-colors duration-150 text-sm font-medium ${
                        isActive
                          ? "bg-blue-100 text-blue-700 font-semibold"
                          : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center flex-1 min-w-0">
                          <Hammer
                            className={`w-4 h-4 mr-3 flex-shrink-0 ${
                              isActive ? "text-blue-600" : "text-gray-400"
                            }`}
                          />
                          <span className="truncate">{project.title}</span>
                        </div>
                        {!isUserQuantitySurveyor && isActive && (
                          <ChevronDown
                            className={`w-4 h-4 ml-2 flex-shrink-0 ${
                              isActive ? "text-blue-600" : "text-gray-400"
                            }`}
                          />
                        )}
                      </div>
                    </Link>
                    {/* Nested sub-navigation when project is active */}
                    {!isUserQuantitySurveyor && isActive && (
                      <div className="ml-4 mt-1 mb-2 space-y-0.5">
                        {[
                          {
                            key: "documentations",
                            label: t("tabs.documentations"),
                            icon: CameraIcon,
                          },
                          {
                            key: "sections",
                            label: t("tabs.sections"),
                            icon: LayoutGrid,
                          },
                          {
                            key: "billofquantities",
                            label: t("tabs.leistungsverzeichnis"),
                            icon: FileText,
                          },
                          {
                            key: "team",
                            label: t("tabs.team"),
                            icon: Users,
                          },
                        ].map((tab) => {
                          const isTabActive = location.pathname.startsWith(
                            `/project/${project._id}/${tab.key}`,
                          );
                          const IconComponent = tab.icon;
                          return (
                            <Link
                              key={tab.key}
                              to={`/project/${project._id}/${tab.key}`}
                              className={`flex items-center py-2 px-4 rounded-md mx-2 text-sm transition-colors duration-150 ${
                                isTabActive
                                  ? "bg-blue-50 text-blue-600 font-medium"
                                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                              }`}
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              <IconComponent className="w-4 h-4 mr-2" />
                              {tab.label}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-3 px-4 text-gray-400 text-sm">
              {t("no_projects_yet")}
            </div>
          )}
        </nav>
      </div>
      {/* Main content */}
      <div className="flex-1 overflow-auto flex flex-col">
        {/* Top navigation */}
        <div className="bg-white border-b px-6 py-3 hidden md:flex justify-end items-center">
          <div className="flex items-center space-x-4">
            <div className="relative">
              {/* Tailwind dropdown for language selection */}
              <div className="relative group">
                <button
                  type="button"
                  className="flex items-center text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold  border transition duration-150 ease-in-out"
                  onClick={() => setShowLangDropdown?.((v) => !v)}
                  id="lang-menu-button"
                  aria-expanded="false"
                  aria-haspopup="true"
                >
                  {i18n.language === "de" ? "🇩🇪 Deutsch" : "🇺🇸 English"}
                  <svg
                    className="ml-2 h-4 w-4 text-blue-400 group-hover:text-blue-600 transition-colors duration-150"
                    width="16"
                    height="16"
                    fill="none"
                    viewBox="0 0 20 20"
                  >
                    <path
                      d="M6 8l4 4 4-4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                {/* Dropdown menu */}
                <div
                  className={`absolute z-50 right-0 mt-2 w-40 bg-white rounded-md border transition-all duration-150 ${
                    showLangDropdown
                      ? "opacity-100 visible"
                      : "opacity-0 invisible"
                  }`}
                  onMouseLeave={() => setShowLangDropdown?.(false)}
                >
                  <button
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50 flex items-center"
                    onClick={() => {
                      changeLanguage("en");
                      setShowLangDropdown?.(false);
                    }}
                  >
                    🇺🇸 English
                  </button>
                  <button
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50 flex items-center"
                    onClick={() => {
                      changeLanguage("de");
                      setShowLangDropdown?.(false);
                    }}
                  >
                    🇩🇪 Deutsch
                  </button>
                </div>
              </div>
            </div>
            <Link
              to="/profile"
              className="flex items-center space-x-2 p-2 rounded-full hover:bg-gray-100"
            >
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white">
                {user?.name ? user.name.charAt(0).toUpperCase() : "U"}
              </div>
              <span className="text-sm font-medium text-gray-700 hidden md:block">
                {user?.name || "User"}
              </span>
            </Link>
          </div>
        </div>
        {/* Page content */}
        <div
          className={`flex-1 min-h-0 min-w-0 flex flex-col overflow-y-auto ${
            !removePadding ? "px-6 py-4" : ""
          }`}
        >
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default AuthedLayout;
